import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from "react";
import type { ExportFormatType } from "../../../common/model/exportFormatType";
import type { UserDto } from "../../../common/model/userDto";
import ConnectedService from "../domain/ConnectedService";
import { ConnectStatus } from "../domain/ConnectStatus";
import User from "../domain/User";
import { useApi } from "./apiContext";
import { useAuth } from "./authContext";
import { useExternalServices } from "./externalServicesContext";
import { useProgress } from "./progressContext";

interface UserContextType {
  user: User | null;
  initializeUserAsync: (forcedRetry: boolean) => Promise<User | null>;
  exportUserDataAsync: (format: ExportFormatType) => Promise<void>;
  finalizeStravaConnectionAsync: () => Promise<void>;
  hasInitializationFailed: boolean;
  initializationError: string | null;
}

export const UserContext = createContext<UserContextType>({
  user: null,
  initializeUserAsync: async (_forcedRetry: boolean) => null,
  exportUserDataAsync: async (_format: ExportFormatType) => {},
  finalizeStravaConnectionAsync: async () => {},
  hasInitializationFailed: false,
  initializationError: null,
});

/**
 * @typedef UserContextProviderProps
 * @property {ReactNode} children - React components that will be rendered as children of the UserContextProviderProps.
 */
interface UserContextProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserContextProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const { viewer } = useAuth();
  const { isInitializing, setInitializingUser, setInitializingServiceConnection } = useProgress();
  const [hasInitializationFailed, setInitializationFailed] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const { strava } = useExternalServices();
  const api = useApi();
  const userOpInProgress = useRef(false); // unreal that this is needed to prevent multiple calls to the same API endpoint

  interface FormatExportedDataResult {
    formattedData: any;
    mimeType: string;
    fileExtension: string;
  }

  function formatExportedData(
    exportedData: string,
    format: ExportFormatType,
  ): FormatExportedDataResult {
    let mimeType = "";
    let fileExtension = "";

    let formattedData = exportedData;
    switch (format) {
      case "json":
        formattedData = JSON.parse(exportedData);
        mimeType = "application/json";
        fileExtension = ".json";
        break;
      case "csv":
        mimeType = "text/csv";
        fileExtension = ".csv";
        break;
    }

    return {
      formattedData,
      mimeType,
      fileExtension,
    };
  }

  function downloadFile(formattedData: string, mimeType: string, fileName: string) {
    const blob = new Blob([formattedData], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    // Clean up
    document.body.removeChild(link);
    // Release the reference to the blob
    window.URL.revokeObjectURL(url);
  }

  const initializeUserAsync = useCallback(
    async (forcedRetry: boolean = false): Promise<User | null> => {
      if (
        userOpInProgress.current ||
        isInitializing() ||
        (!forcedRetry && hasInitializationFailed)
      ) {
        return null;
      }

      if (!viewer) {
        setInitializationFailed(true);
        setInitializationError("You need an active session before the app can initialize.");
        return null;
      }

      const createUserOf = (): UserDto => {
        return {
          userId: viewer.id,
          displayName: viewer.name,
          email: viewer.email,
          userName: viewer.userName,
          connectedServices: [],
        };
      };
      const userInfo: UserDto = createUserOf();

      if (user && userInfo.userId === user.id) {
        return user;
      }

      setInitializingUser(true);
      try {
        const initializedUser = await api.initializeUserAsync(userInfo);
        const userRef = User.fromDto(initializedUser);
        setUser(userRef);
        setInitializationFailed(false);
        setInitializationError(null);
        return userRef;
      } catch (error) {
        setInitializationFailed(true);
        setInitializationError(
          error instanceof Error ? error.message : "Unknown initialization error",
        );
        console.error(error);
        return null;
      } finally {
        setInitializingUser(false);
      }
    },
    [api, isInitializing, viewer, hasInitializationFailed, user, setInitializingUser],
  );

  const finalizeStravaConnectionAsync = useCallback(async () => {
    if (!isInitializing() && !userOpInProgress.current && strava.status !== ConnectStatus.Ready) {
      userOpInProgress.current = true;
      try {
        setInitializingServiceConnection(true);
        const connectedService = await strava.finishConnectAsync();
        if (connectedService && user) {
          const nextConnectedServices = user.connectedServices
            .filter((service) => service.serviceName !== connectedService.serviceName)
            .concat(ConnectedService.fromDto(connectedService));
          setUser(new User(user.id, user.name, user.email, user.userName, nextConnectedServices));
          setInitializationFailed(false);
          setInitializationError(null);
        } else if (connectedService) {
          await initializeUserAsync(true);
        }
      } finally {
        setInitializingServiceConnection(false);
        userOpInProgress.current = false;
      }
    }
  }, [initializeUserAsync, setInitializingServiceConnection, strava, isInitializing, user]);

  const getFormattedDate = () => {
    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(
      currentDate.getDate(),
    ).padStart(2, "0")}`;
    return formattedDate;
  };

  const exportUserDataAsync = async (format: ExportFormatType) => {
    try {
      if (!user) {
        return;
      }

      const userDataExportString =
        format === "json"
          ? JSON.stringify(user, null, 2)
          : `id,name,email,userName\n${user.id},${user.name},${user.email},${user.userName}`;

      const { formattedData, mimeType, fileExtension } = formatExportedData(
        userDataExportString,
        format,
      );

      const formattedDate = getFormattedDate();
      downloadFile(formattedData, mimeType, `user-data-${formattedDate}${fileExtension}`);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        initializeUserAsync,
        exportUserDataAsync,
        finalizeStravaConnectionAsync,
        hasInitializationFailed,
        initializationError,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within an UserProvider");
  }
  return context;
};
